import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, MailCheck, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "../lib/api";

/**
 * FT-6 — GC schedule confirmations. The system queues every mobilization inside the lead window
 * with a ready draft (PM identity, GC recipients); a person reviews and sends. While the test
 * address is set, every send goes there instead. Renders nothing while the switch is off.
 */
export default function GcConfirmPanel({ projectId }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const load = async () => {
    try { setData((await api.get(`/gc-confirm/projects/${projectId}/queue`)).data); } catch { setData(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  if (!data?.enabled || !data.queue.length) return null;

  const open = async (mobId) => {
    try { setDraft((await api.get(`/gc-confirm/mobilizations/${mobId}/draft`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not prepare the email"); }
  };

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs space-y-1" data-testid="gc-confirm-panel">
      <div className="flex items-center gap-2">
        <Mail className="w-4 h-4 text-sky-700" />
        <span className="font-semibold text-sky-900">
          GC schedule confirmations — {data.pending} of {data.queue.length} in the next {data.lead_days} days still to send
        </span>
        {data.test_email && (
          <span className="ml-auto rounded bg-amber-100 border border-amber-300 text-amber-900 px-1.5 py-0.5 font-semibold" data-testid="gc-confirm-test-badge">
            TEST MODE → {data.test_email}
          </span>
        )}
      </div>
      <ul className="space-y-0.5">
        {data.queue.map((q) => (
          <li key={q.mobilization_id} className="flex items-center gap-2 text-sky-900" data-testid={`gc-confirm-row-${q.mobilization_id}`}>
            {q.confirmed ? <MailCheck className="w-3.5 h-3.5 text-emerald-700" /> : <Mail className="w-3.5 h-3.5 text-sky-600" />}
            <span className="truncate">
              <b>{q.title}</b> · {q.location} · starts {q.scheduled_start}
              {q.confirmed && <span className="text-emerald-700"> — confirmed {q.last_sent?.at?.slice(0, 10)}</span>}
              {q.moved_since && !q.confirmed && <span className="text-amber-800"> — date moved since the last email</span>}
            </span>
            <Button size="sm" variant={q.confirmed ? "ghost" : "outline"} className="ml-auto h-7 text-xs shrink-0"
                    onClick={() => open(q.mobilization_id)} data-testid={`gc-confirm-review-${q.mobilization_id}`}>
              {q.confirmed ? "Send again" : "Review & send"}
            </Button>
          </li>
        ))}
      </ul>
      {draft && <ReviewDialog draft={draft} onClose={() => setDraft(null)} onSent={() => { setDraft(null); load(); }} />}
    </div>
  );
}

function ReviewDialog({ draft, onClose, onSent }) {
  const [to, setTo] = useState(draft.to.join(", "));
  const [cc, setCc] = useState(draft.cc.join(", "));
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [busy, setBusy] = useState(false);
  const split = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/gc-confirm/mobilizations/${draft.mobilization.id}/send`, { to: split(to), cc: split(cc), subject, body });
      toast.success(draft.test_email ? `Test sent to ${draft.test_email}` : `Sent to ${r.data.sent.join(", ")}`);
      onSent();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "Not sent");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="gc-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Review before send — {draft.mobilization.title}</DialogTitle>
          <DialogDescription>
            From {draft.from_name} &lt;{draft.from_email}&gt; · reply-to {draft.reply_to}
            {draft.mechanism === "department_with_reply_to" && " (department mailbox, PM on reply-to)"}
          </DialogDescription>
        </DialogHeader>
        {draft.test_email && (
          <div className="rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-xs p-2" data-testid="gc-confirm-test-notice">
            <b>Test mode.</b> This goes to <b>{draft.test_email}</b> only; the real recipients are named in the subject. Clear the test address in Settings to send to the GC.
          </div>
        )}
        {!draft.to.length && (
          <div className="rounded-md bg-red-50 border border-red-200 text-red-900 text-xs p-2" data-testid="gc-confirm-no-recipient">
            No GC project manager is on file for this customer — type an address, or add the contact in the Directory.
          </div>
        )}
        <div className="grid gap-2 text-xs">
          <label>To<Input value={to} onChange={(e) => setTo(e.target.value)} className="h-8 mt-0.5" data-testid="gc-confirm-to" /></label>
          <label>Cc<Input value={cc} onChange={(e) => setCc(e.target.value)} className="h-8 mt-0.5" data-testid="gc-confirm-cc" /></label>
          <label>Subject<Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-8 mt-0.5" data-testid="gc-confirm-subject" /></label>
          <label>Body<Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} className="mt-0.5 text-xs" data-testid="gc-confirm-body" /></label>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="gc-confirm-cancel">Cancel</Button>
          <Button size="sm" className="bg-sky-700 hover:bg-sky-800" disabled={busy || !split(to).length} onClick={send} data-testid="gc-confirm-send-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            {draft.test_email ? "Send test" : "Send to GC"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
